import React, {useCallback, useEffect, useRef, useState} from 'react';
import {GridSortDirection, GridSortModel} from '@mui/x-data-grid';
import {useTranslation} from 'react-i18next';

import {
  deleteHeaderAnalysisRule,
  resolveHeaderAnalysisRules,
  updateHeaderAnalysisRule,
} from '../../../api/emailTriage';
import {useCallbackPrompt} from '../../../hooks/useCallbackPrompt';
import {
  isHeaderAnalysisRuleReadOnly,
  mapPersistedHeaderAnalysisRule,
} from '../headerAnalysisPresentation';

const PERPAGES = 10;

const DEFAULT_TAB_FILTER_VALUES = {
  perPages: PERPAGES,
  page: 1,
  keyword: '',
  emailChecks: [],
  sortDir: 'desc',
  sortCol: 'threat_score',
  dropdownFilters: {emailChecks: null},
};

const GRID_SORT_COLUMNS: Record<string, string> = {
  ruleName: 'rule_name',
  description: 'description',
  threatScoreContribution: 'threat_score',
  lastUpdated: 'last_updated',
  addedBy: 'added_by',
  status: 'status',
};

const API_SORT_COLUMNS: Record<string, string> = Object.fromEntries(
  Object.entries(GRID_SORT_COLUMNS).map(([gridColumn, apiColumn]) => [apiColumn, gridColumn]),
);

type CheckMap = {[id: string]: boolean};

const toStatusStr = (value: boolean) => (value ? 'active' : 'inactive');

const getChangedExceptionUpdated = (
  headerAnalysisData: any[],
  check: CheckMap,
) => {
  const changed: any[] = [];
  for (const item of headerAnalysisData) {
    if (Object.prototype.hasOwnProperty.call(check, item.id)) {
      const newStatus = toStatusStr(check[item.id]);
      if (item.status !== newStatus) {
        changed.push({id: item.id, status: newStatus});
      }
    }
  }
  return changed;
};

const editableRulePayload = (rule: any, enabled: boolean) => ({
  name: rule.name,
  description: rule.description,
  header_rule_score: rule.header_rule_score,
  category: rule.category,
  enabled,
  condition_group: rule.condition_group,
  actions: rule.actions,
});

export const useHeaderAnalysisListController = (globalZone = false) => {
  // ---------------------------------------------------------------------------
  // Rule data, table state, and feedback
  // ---------------------------------------------------------------------------
  const allRuleRowsRef = useRef<any[]>([]);
  const [ruleRows, setRuleRows] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const actionsOpen = Boolean(anchorEl);
  const [currentActionSelection, setCurrentActionSelection] = useState('');
  const [isShowLoading, setShowLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [sortCol, setSortCol] = useState(DEFAULT_TAB_FILTER_VALUES.sortCol);
  const [sortDir, setSortDir] = useState(DEFAULT_TAB_FILTER_VALUES.sortDir);
  const [sortModel, setSortModel] = useState<GridSortModel>([{
    field: 'threatScoreContribution',
    sort: 'desc',
  }]);
  const [isActionShow, setActionShow] = React.useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState<boolean>(false);
  const [toastValue, setToastValue] = useState({type: '', message: '', isOpen: false, toastId: 0});
  const [modalID, setModalID] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(DEFAULT_TAB_FILTER_VALUES.page);
  const [perPages, setPerPages] = useState(DEFAULT_TAB_FILTER_VALUES.perPages);
  const currentPageRef = useRef(currentPage);
  const perPagesRef = useRef(perPages);
  const [startPoint, setStartPoint] = useState(0);
  const [endPoint, setEndPoint] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  // ---------------------------------------------------------------------------
  // Unsaved changes and list-control state
  // ---------------------------------------------------------------------------
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(showLeaveDialog);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedItems, setSelectedItems] = useState<any>({emailChecks: []});
  const [filterSearchKeyword, setFilterSearchKeyword] = useState(DEFAULT_TAB_FILTER_VALUES.keyword);
  const [pendingFilterSearchKeyword, setPendingFilterSearchKeyword] = useState(filterSearchKeyword);
  const [checked, setChecked] = useState<CheckMap>({});
  const [initChecked, setInitChecked] = useState<CheckMap>({});
  const [openUnsavedConfirm, setOpenUnsavedConfirm] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [pendingPage, setPendingPage] = useState(currentPage);
  const [pendingPerPage, setPendingPerPage] = useState(DEFAULT_TAB_FILTER_VALUES.perPages);
  const [filterResetPending, setFilterResetPending] = useState(false);
  const [pendingFilterDropdownOpen, setPendingFilterDropdownOpen] = useState(
    DEFAULT_TAB_FILTER_VALUES.dropdownFilters,
  );
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(
    DEFAULT_TAB_FILTER_VALUES.dropdownFilters,
  );
  const {t} = useTranslation();
  const pendingKeywordRef = useRef<any>(null);
  const isReadOnlyRule = (row: any) => isHeaderAnalysisRuleReadOnly(row, globalZone);
  currentPageRef.current = currentPage;
  perPagesRef.current = perPages;

  const applyPageRows = useCallback((rows: any[], page: number, pageSize: number) => {
    const nextTotalRows = rows.length;
    const nextTotalPages = Math.max(1, Math.ceil(nextTotalRows / pageSize));
    const nextPage = Math.min(page, nextTotalPages);
    const offset = (nextPage - 1) * pageSize;
    const displayedRows = rows.slice(offset, offset + pageSize);
    const nextChecked = displayedRows.reduce((result: CheckMap, row: any) => ({
      ...result, [row.id]: row.status === 'active',
    }), {});

    setRuleRows(displayedRows);
    setInitChecked(nextChecked);
    setChecked(nextChecked);
    setTotalRows(nextTotalRows);
    setTotalPages(nextTotalPages);
    setStartPoint(displayedRows.length ? offset + 1 : 0);
    setEndPoint(displayedRows.length ? offset + displayedRows.length : 0);
    if (nextPage !== page) setCurrentPage(nextPage);
  }, []);

  // ---------------------------------------------------------------------------
  // Rule loading and refresh
  // ---------------------------------------------------------------------------
  const getData = useCallback(async () => {
    setLoadingPage(true);
    try {
      const response: any = await resolveHeaderAnalysisRules({
        keyword: filterSearchKeyword,
        filters: {
          email_checks: (selectedItems.emailChecks || []).map(
            (check: string) => check.toLowerCase(),
          ),
        },
        sort_col: sortCol,
        sort_dir: sortDir,
        unpaginated: true,
      });
      const data = response?.data || response;
      const rows = (data?.rules || []).map((rule: any) => (
        mapPersistedHeaderAnalysisRule(rule, globalZone)
      ));
      allRuleRowsRef.current = rows;
      applyPageRows(rows, currentPageRef.current, perPagesRef.current);
    } catch (error: any) {
      setToastValue({
        type: 'error',
        message: error?.response?.data?.detail || 'Unable to load Threat Score Rules.',
        isOpen: true,
        toastId: Date.now(),
      });
    } finally {
      setLoadingPage(false);
    }
  }, [
    applyPageRows,
    currentPage,
    filterSearchKeyword,
    globalZone,
    perPages,
    selectedItems.emailChecks,
    sortCol,
    sortDir,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    applyPageRows(allRuleRowsRef.current, currentPage, perPages);
  }, [applyPageRows, currentPage, perPages]);

  // ---------------------------------------------------------------------------
  // List-control synchronization effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isActionShow && filterResetPending) {
      resetFilters();
    }
  }, [isActionShow, filterResetPending]);

  // ---------------------------------------------------------------------------
  // Search, filter, sorting, and pagination controls
  // ---------------------------------------------------------------------------
  const resetFilters = () => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setFilterResetPending(true);
    } else {
      const {keyword, perPages, emailChecks, page, sortCol, sortDir} = DEFAULT_TAB_FILTER_VALUES;
      setFilterSearchKeyword(keyword);
      setPendingFilterSearchKeyword(keyword);
      setPerPages(perPages);
      setSelectedItems({emailChecks});
      setCurrentPage(page);
      setSortCol(sortCol);
      setSortDir(sortDir);
      setSortModel([{
        sort: sortDir as GridSortDirection,
        field: API_SORT_COLUMNS[sortCol],
      }]);
      setFilterResetPending(false);
    }
  };

  const handlePerPageRowsChange = (event: any) => {
    const value = parseInt(event.target.value);
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingPerPage(value);
    } else {
      setPerPages(value);
      if (currentPage > 1) {
        setCurrentPage(1);
      }
    }
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingPage(page);
    } else {
      setCurrentPage(page);
    }
  };

  // ---------------------------------------------------------------------------
  // Status draft, save, and discard workflow
  // ---------------------------------------------------------------------------
  const changeStatusRule = (status: any, row: any) => {
    const isChecked = status.target.checked;
    setChecked({
      ...checked,
      [row.id]: isChecked,
    });
    setActionShow(true);
    setShowLeaveDialog(true);
  };

  const onSort = (model: any) => {
    if (!model?.length || !GRID_SORT_COLUMNS[model[0].field]) return;
    setSortDir(model[0].sort);
    setSortCol(GRID_SORT_COLUMNS[model[0].field]);
    setSortModel(model);
    setCurrentPage(1);
    setActionShow(false);
    setShowLeaveDialog(false);
  };

  const handleDiscard = () => {
    setActionShow(false);
    setShowLeaveDialog(false);
    setChecked(initChecked);
  };

  const handleSaveChange = async () => {
    setShowLoading(true);
    try {
      const updatedItems = getChangedExceptionUpdated(ruleRows, checked);
      for (const item of updatedItems) {
        const row = ruleRows.find(candidate => candidate.id === item.id);
        await updateHeaderAnalysisRule(item.id, {
          rule: editableRulePayload(row.rawRule, item.status === 'active'),
        });
      }
      setModalID(modalID + 1);
      setToastValue({type: 'success', message: t('messages.genericSaved'), isOpen: true, toastId: modalID});
      setActionShow(false);
      setShowLeaveDialog(false);
      await getData();
    } catch (error: any) {
      setToastValue({
        type: 'error',
        message: error?.response?.data?.detail || 'Unable to update the selected rules.',
        isOpen: true,
        toastId: Date.now(),
      });
      // A prior update in this sequential batch may already have committed.
      // Re-resolve rows before allowing the administrator to retry the batch.
      await getData();
      setActionShow(false);
      setShowLeaveDialog(false);
    } finally {
      setShowLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Row action menu and delete workflow
  // ---------------------------------------------------------------------------
  const hideActionsDropdown = () => {
    const gridRows = document.querySelectorAll('.MuiDataGrid-row');
    if (gridRows) {
      for (let i = 0; i < gridRows.length; i++) {
        gridRows[i].classList.remove('focusing');
      }
    }
    setAnchorEl(null);
  };

  const handleActionDelete = () => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingDelete(true);
    } else {
      setOpenDelete(true);
    }
  };

  const prepareActionEdit = (row?: any) => {
    if (row && isReadOnlyRule(row)) return undefined;
    const ruleId = row?.id ?? currentActionSelection;
    if (isActionShow) {
      setShowLeaveDialog(true);
      setPendingEdit(true);
    }
    return ruleId;
  };

  const openActionsDropdown = (anchor: HTMLElement, ruleId: any) => {
    setCurrentActionSelection(ruleId);
    setAnchorEl(anchor);
  };

  const handleCloseModal = () => {
    setOpenDelete(false);
    hideActionsDropdown();
  };

  const handleDeleteEntry = async () => {
    setShowLoading(true);
    try {
      await deleteHeaderAnalysisRule(String(currentActionSelection), {});
      setModalID(modalID + 1);
      setToastValue({
        type: 'success', message: t('messages.threatExceptions.deleteSuccess'),
        isOpen: true, toastId: modalID,
      });
      await getData();
    } catch (error: any) {
      setToastValue({
        type: 'error',
        message: error?.response?.data?.detail || 'Unable to delete this rule.',
        isOpen: true,
        toastId: Date.now(),
      });
    } finally {
      setShowLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Search and filter placeholder interactions
  // ---------------------------------------------------------------------------
  const handleFilter = (item: any, type: any) => {
    setSelectedItems((previous: any) => ({
      ...previous,
      [type]: item,
    }));
    setCurrentPage(1);
  };

  const onSearchInboxByKeywords = (event: any) => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
    } else if (event.keyCode === 13) {
      setFilterSearchKeyword(pendingFilterSearchKeyword.trim());
      setCurrentPage(1);
    }
  };

  const handleClearFilter = (type: any) => {
    setSelectedItems((previous: any) => ({
      ...previous,
      [type]: [],
    }));
  };

  const dropdownFilterOpenHandler = (value: any) => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingFilterDropdownOpen(value);
    } else {
      setFilterDropdownOpen(value);
    }
  };

  // ---------------------------------------------------------------------------
  // Pending actions and unsaved-change coordination
  // ---------------------------------------------------------------------------
  const resetPendingStates = () => {
    setPendingDelete(false);
    setPendingEdit(false);
    setPendingPage(currentPage);
    setPendingPerPage(perPages);
    setPendingFilterDropdownOpen(DEFAULT_TAB_FILTER_VALUES.dropdownFilters);
  };

  useEffect(() => {
    const shouldResetPending = openDelete || currentPage === pendingPage ||
      perPages === pendingPerPage ||
      JSON.stringify(filterDropdownOpen) !== JSON.stringify(pendingFilterDropdownOpen);
    if (shouldResetPending) {
      resetPendingStates();
    }
  }, [openDelete, currentPage, perPages, filterDropdownOpen]);

  const handleContinueWhenUnsaved = () => {
    setFilterDropdownOpen(pendingFilterDropdownOpen);
    setOpenDelete(pendingDelete);
    pendingKeywordRef?.current?.focus();
    if (pendingPage !== currentPage) {
      setCurrentPage(pendingPage);
    }
    if (pendingPerPage !== perPages) {
      setPerPages(pendingPerPage);
    }
    setOpenUnsavedConfirm(false);
    setActionShow(false);
    setShowLeaveDialog(false);
    setChecked(initChecked);
  };

  const handleCancelWhenUnsaved = () => {
    resetPendingStates();
    setOpenUnsavedConfirm(false);
    setAnchorEl(null);
  };

  const handleKeywordFocus = (event?: React.FocusEvent<HTMLInputElement>) => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      event?.preventDefault();
    }
  };

  // ---------------------------------------------------------------------------
  // Public controller contract
  // ---------------------------------------------------------------------------
  return {
    actionsOpen,
    anchorEl,
    changeStatusRule,
    checked,
    confirmNavigation,
    cancelNavigation,
    currentPage,
    dropdownFilterOpenHandler,
    endPoint,
    filterDropdownOpen,
    handleActionDelete,
    handleCancelWhenUnsaved,
    handleClearFilter,
    handleCloseModal,
    handleContinueWhenUnsaved,
    handleDeleteEntry,
    handleDiscard,
    handleFilter,
    handleKeywordFocus,
    handlePageChange,
    handlePerPageRowsChange,
    handleSaveChange,
    hideActionsDropdown,
    isActionShow,
    isReadOnlyRule,
    isShowLoading,
    loadingPage,
    onSearchInboxByKeywords,
    onSort,
    openActionsDropdown,
    openDelete,
    openUnsavedConfirm,
    pendingFilterSearchKeyword,
    pendingKeywordRef,
    perPages,
    prepareActionEdit,
    resetFilters,
    ruleRows,
    selectedItems,
    setActionShow,
    setPendingFilterSearchKeyword,
    showPrompt,
    sortModel,
    startPoint,
    t,
    toastValue,
    totalPages,
    totalRows,
  };
};
